import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../api";
import {
  Button,
  ConfirmDialog,
  Field,
  Input,
  Loading,
  Status,
} from "../../components/ui";
import { Feedback, LoadError, useAction, useResource } from "./shared";

type Integration = {
  enabled: boolean;
  plugin_zip_download_url: string;
  integration_key?: string;
};
export default function IntegrationSettings() {
  const { t } = useTranslation("settings");
  const api = useApi();
  const remote = useResource<Integration>(
    "/organizations/me/integrations/woocommerce",
  );
  const action = useAction();
  const [key, setKey] = useState<string | null>(null);
  const [confirmRotate, setRotate] = useState(false);
  const generate = (rotate = false) =>
    action.run(
      async () => {
        const result = await api.post(
          `/organizations/me/integrations/woocommerce/key${rotate ? "/rotate" : ""}`,
          {},
        );
        setKey(result.integration_key || null);
        setRotate(false);
        await remote.reload();
      },
      t(rotate ? "integration.rotated" : "integration.generated"),
    );
  const download = () =>
    void action.run(async () => {
      const response = await api.getResponse(
        remote.data?.plugin_zip_download_url ||
          "/organizations/me/integrations/woocommerce/plugin.zip",
      );
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = "valid-labels-clp.zip";
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, t("integration.downloaded"));
  return (
    <section className="settings-section settings-form-width">
      <div className="settings-section-heading">
        <h2>WooCommerce</h2>
        {remote.data && (
          <Status tone={remote.data.enabled ? "success" : "neutral"}>
            {t(
              remote.data.enabled
                ? "integration.connected"
                : "integration.notConfigured",
            )}
          </Status>
        )}
      </div>
      <p className="settings-muted">{t("integration.description")}</p>
      <LoadError {...remote} />
      <Feedback {...action} />
      {remote.data?.enabled && (
        <p className="settings-muted">{t("integration.setupStillNeeded")}</p>
      )}
      {remote.loading && !remote.data ? (
        <Loading label={t("common.loading")} />
      ) : (
        <>
          <div className="settings-inline">
            {!remote.data?.enabled && (
              <Button
                disabled={action.busy || Boolean(remote.error)}
                onClick={() => void generate()}
              >
                {t("integration.generate")}
              </Button>
            )}
            <Button
              variant={remote.data?.enabled ? "default" : "outline"}
              disabled={action.busy}
              onClick={download}
            >
              {t("integration.download")}
            </Button>
            {remote.data?.enabled && (
              <Button
                variant="ghost"
                disabled={action.busy}
                onClick={() => setRotate(true)}
              >
                {t("integration.rotate")}
              </Button>
            )}
          </div>
          {key && (
            <div className="settings-secret">
              <Field
                label={t("integration.key")}
                htmlFor="integration-key"
                hint={t("integration.once")}
              >
                <Input
                  id="integration-key"
                  value={key}
                  readOnly
                  autoComplete="off"
                  spellCheck={false}
                />
              </Field>
              <div className="settings-inline">
                <Button
                  variant="outline"
                  onClick={() =>
                    void action.run(
                      () => navigator.clipboard.writeText(key),
                      t("common.copied"),
                    )
                  }
                >
                  {t("common.copy")}
                </Button>
                <Button variant="ghost" onClick={() => setKey(null)}>
                  {t("integration.hide")}
                </Button>
              </div>
            </div>
          )}
          <h3>{t("integration.install")}</h3>
          <ol className="settings-steps">
            <li>{t("integration.step1")}</li>
            <li>{t("integration.step2")}</li>
            <li>{t("integration.step3")}</li>
            <li>{t("integration.step4")}</li>
            <li>{t("integration.step5")}</li>
          </ol>
        </>
      )}
      <ConfirmDialog
        open={confirmRotate}
        onOpenChange={setRotate}
        title={t("integration.rotate")}
        description={t("integration.rotateWarning")}
        confirmLabel={t("integration.rotate")}
        cancelLabel={t("common.cancel")}
        pending={action.busy}
        onConfirm={() => generate(true).then(() => undefined)}
      />
    </section>
  );
}
